import { z } from "zod";
import { priceOverviewSchema } from "./price-overview";

// TES-286 [J5] — the "Vad händer just nu" (§2 of Home) prioritization engine.
//
// §2 is a PRIORITY surface: it shows exactly ONE prioritized object, chosen
// server-side from ALL sources by a fixed 7-tier ladder. It is distinct from the
// `/today` list (which shows several due tasks) — home_now answers the single
// question "what is the ONE thing that matters most right now".
//
// A candidate is a PROJECTION, never a stored row: the engine merges authorable
// FlowAssignmentItems placed on the `home_now` surface with a set of DERIVED
// producers (risk, blocking task, unseen clinician feedback, reviews, weekly
// check-in, program change) into one ranked list. The top non-fallback candidate
// is §2; the calm `fallback` item is shown only when nothing needs attention.

// The candidate taxonomy, ordered by the tier ladder below. Each type is sourced
// by exactly one producer in HomeNowService (some still stubbed — see
// HOME_NOW_TIERS for the human labels + the service for what's real vs deferred).
export const HOME_NOW_CANDIDATE_TYPES = [
  "acute_medical_risk", // tier 1
  "blocking_task", // tier 2
  // TES-1428 (TES-1425 §1, §6) — "Din behandlingsvecka": the planned dose
  // and the week's check-in (F-04, or F-05 in a month week) as one card with
  // two `actions`, each with its own status. Served only to a build at or
  // after CLIENT_CONTRACT_CHANGES.treatmentWeekCard; older builds keep the
  // separate dose-day, weekly_checkin and review_due cards.
  "treatment_week", // tier 2 while the dose is open, see HOME_NOW_TIERS
  "meeting", // tier 2 — a booked/imminent consult (TES-354); required blocking
  // tasks outrank it within the tier (the doctor needs the declaration BEFORE
  // the visit), everything below yields to it.
  "clinician_feedback", // tier 3
  "review_due", // tier 4
  "weekly_checkin", // tier 5
  "program_change", // tier 6
  "motivation", // tier 7 — authorable home_now content items
  // TES-527 (CP-14) — "Din återkoppling är sparad": a low-priority status
  // card after an early stop the patient closed for now. Never above real
  // care tasks or safety cards, never shown alongside CP-01.
  "f01_result_saved", // tier 7
] as const;
export const homeNowCandidateTypeSchema = z.enum(HOME_NOW_CANDIDATE_TYPES);
export type HomeNowCandidateType = z.infer<typeof homeNowCandidateTypeSchema>;

// The fixed tier ladder, exported as DATA (candidateType → tier + a human
// label). Tier 1 is the most urgent; the engine sorts tier ASC so a lower tier
// number always wins. The label is display copy for the admin/debug surfaces.
export const HOME_NOW_TIERS: readonly {
  tier: number;
  candidateType: HomeNowCandidateType;
  label: string;
}[] = [
  {
    tier: 1,
    candidateType: "acute_medical_risk",
    label: "Akut medicinsk risk",
  },
  { tier: 2, candidateType: "blocking_task", label: "Blockerande uppgift" },
  // TES-1428 — the card ranks where its most urgent OPEN action ranked as a
  // separate card: the dose task's tier 2 while the dose is open, then the
  // F-05's tier 4 or the F-04's tier 5. Merging the cards leaves every other
  // task's place unchanged (TES-1425 §10). The ladder names the dose-day case.
  {
    tier: 2,
    candidateType: "treatment_week",
    label: "Din behandlingsvecka",
  },
  // TES-354 — the booked-consult card moved from a client-side override into
  // the ladder ("ett objekt" = ONE prioritization authority). Same tier as
  // blocking_task; required-first tie-break lets the declaration/labs win.
  { tier: 2, candidateType: "meeting", label: "Bokat besök" },
  {
    tier: 3,
    candidateType: "clinician_feedback",
    label: "Återkoppling från vården",
  },
  { tier: 4, candidateType: "review_due", label: "Dags för uppföljning" },
  { tier: 5, candidateType: "weekly_checkin", label: "Veckoincheckning" },
  { tier: 6, candidateType: "program_change", label: "Ändring i din plan" },
  { tier: 7, candidateType: "motivation", label: "Motivation & innehåll" },
  {
    tier: 7,
    candidateType: "f01_result_saved",
    label: "Sparad återkoppling (CP-14)",
  },
] as const;

// candidateType → tier lookup, derived from the ladder above (single source).
export const HOME_NOW_TIER_BY_TYPE: Record<HomeNowCandidateType, number> =
  Object.fromEntries(
    HOME_NOW_TIERS.map((t) => [t.candidateType, t.tier]),
  ) as Record<HomeNowCandidateType, number>;

// TES-1286 (TES-1269 points 1–4, CP-06/09C "SEPARATA PATIENTUPPGIFTER"
// §3, §6) — a task card may carry several actions in a fixed order, e.g. the
// paid blood test's "Hitta provtagningsställe", "Så förbereder du dig",
// "Jag har lämnat blodprovet". `deep_link` navigates to `path` like a
// `ctaPath`; the other kinds are performed in place on Hem (the existing
// report/undo endpoints, with the same confirm dialog as the checklist).
//
// Additive and ungated: a card with `actions` keeps a single
// `ctaLabel`/`ctaPath` that every installed build can follow (the CP-06
// checklist), and builds before 2.0.9 never read `actions`. A 2.0.9 build
// renders `actions` when present and falls back to the single CTA otherwise.
//
// TES-1297 — `resend_subscription_info` is CP-11's "Skicka informationen
// igen": POST subscription-link/resend, the same transactional e-mail the
// Dina steg rung re-sends. Never a browser, a checkout or a link.
//
// TES-1646 (Ö-1015) — `choose_written_assessment` and `choose_care_meeting`
// are the two answers on Hem's assessment choice after the health
// declaration: POST patient-api/v1/me/assessment-choice with "written" or
// "meeting", then re-read Hem. The meeting one also opens its `path` (the
// existing booking), so she does not have to press a second button. Served
// only to a build at or after CLIENT_CONTRACT_CHANGES.assessmentChoice.
export const HOME_NOW_ACTION_KINDS = [
  "deep_link",
  "report_blood_sample",
  "undo_blood_sample",
  "resend_subscription_info",
  "choose_written_assessment",
  "choose_care_meeting",
  // TES-1663 — the negative F-01 feedback in "Ditt medicinska team": "Boka
  // vårdmöte – 395 kr" records her booking decision on the result (POST
  // f01/decision "book", as the result page's "Boka vårdmöte") and then opens
  // the booking (`path`); "Avsluta för nu" records "close_for_now". Sent only
  // in the onboarding guide, to a build at
  // CLIENT_CONTRACT_CHANGES.negativeF01Channel.
  "f01_book_meeting",
  "f01_close_for_now",
] as const;
export const homeNowActionKindSchema = z.enum(HOME_NOW_ACTION_KINDS);
export type HomeNowActionKind = z.infer<typeof homeNowActionKindSchema>;

// `primary` is the card's main button, `secondary` a full-width secondary
// button, `link` a quiet text-weight action (e.g. "Om ditt blodprov").
export const HOME_NOW_ACTION_EMPHASES = [
  "primary",
  "secondary",
  "link",
] as const;

// TES-1428 (TES-1425 §1) — the two actions of "Din behandlingsvecka". Each
// has its own status, completion event, destination, audit and data source:
//   dose            — her dose log (`dose_events`, `dose_reported`),
//                     carebyella://dose
//   weekly_checkin  — the F-04 FormSubmission (`check_in_submitted`),
//                     carebyella://weekly-checkin
//   monthly_checkin — the F-05 FormSubmission, carebyella://milestone/<n>;
//                     replaces weekly_checkin in a month week, and an
//                     overdue F-05 stays as this action in the weeks after
export const TREATMENT_WEEK_ACTION_KEYS = [
  "dose",
  "weekly_checkin",
  "monthly_checkin",
] as const;
export type TreatmentWeekActionKey =
  (typeof TREATMENT_WEEK_ACTION_KEYS)[number];

// `done` stays on the card, shown as completed, while the other action is
// still open (TES-1425 §1: "När en handling är klar visas den som genomförd
// medan den andra ligger kvar").
export const HOME_NOW_ACTION_STATUSES = ["open", "done"] as const;
export type HomeNowActionStatus = (typeof HOME_NOW_ACTION_STATUSES)[number];

export const homeNowActionSchema = z.object({
  label: z.string().min(1),
  kind: homeNowActionKindSchema,
  // The destination for `deep_link`, and the booking `choose_care_meeting`
  // opens after it is stored; null for the other in-place kinds.
  path: z.string().nullable(),
  emphasis: z.enum(HOME_NOW_ACTION_EMPHASES),
  // TES-1428 — set on the treatment_week card's actions only, absent on
  // every other card's actions (TES-1286). `label` is then the action's CTA
  // ("Registrera dos"), `title` the line above it ("Ta och registrera
  // veckans dos"), `body` optional text under it (the F-05's), and `status`
  // whether it is done.
  key: z.enum(TREATMENT_WEEK_ACTION_KEYS).optional(),
  title: z.string().min(1).optional(),
  body: z.string().min(1).optional(),
  status: z.enum(HOME_NOW_ACTION_STATUSES).optional(),
});
export type HomeNowAction = z.infer<typeof homeNowActionSchema>;

// One ranked candidate for §2. Locale-resolved copy (title/body/cta), PHI-lean:
// only the copy the patient sees plus the ranking metadata.
export const homeNowCandidateSchema = z.object({
  id: z.string(),
  candidateType: homeNowCandidateTypeSchema,
  // Position on the 7-tier ladder (1 = most urgent).
  tier: z.number().int().min(1).max(7),
  // Authorable 1-100 rank; the tie-break WITHIN a tier (higher wins).
  priority: z.number().int().min(1).max(100),
  // A required candidate pins §2 until resolved (ranks above non-required in
  // its tier).
  required: z.boolean(),
  // Surfaced prominently as a blocking task. SURFACE-ONLY for MVP (Option A):
  // does NOT gate journeyState.advance().
  blocksNextState: z.boolean(),
  // ISO datetime the candidate stops being eligible (from the item's relative
  // durationDays window), or null for no expiry.
  expiresAt: z.string().nullable(),
  surface: z.literal("home_now"),
  title: z.string(),
  body: z.string(),
  ctaLabel: z.string().nullable().optional(),
  ctaPath: z.string().nullable().optional(),
  // The source flow key for authorable candidates; null for derived producers.
  sourceFlowKey: z.string().nullable().optional(),
  // TES-1286 — the card's actions in display order (see homeNowActionSchema).
  // Absent on every card that has only its one CTA, and on older APIs.
  actions: z.array(homeNowActionSchema).optional(),
  // TES-1461 (TES-1458 §1, Emelie 2026-10-20) — the shared price overview,
  // "Vad kostar mötet och behandlingen?", which the app shows as a closed
  // dropdown directly under the CTA ("Välj tid"). Set on CP-01's booking card
  // only, and only for a build at or after
  // CLIENT_CONTRACT_CHANGES.hemPriceOverview; that card's `body` then has no
  // "Digitalt vårdmöte · 395 kr" line. Every older build keeps the line.
  priceOverview: priceOverviewSchema.optional(),
  // QA-fix 2026-08-25: the meeting candidate carries STRUCTURED
  // status-card fields so the client can render date/time as the visually
  // primary line ("Tisdag 1 september · 14.30") instead of prose — the date
  // no longer lives embedded in `body`. Optional: absent on every other
  // candidateType and on older API responses.
  meeting: z
    .object({
      scheduledAt: z.string(), // ISO instant
      dateTimeLabel: z.string(), // locale-resolved "tisdag 1 september · 14:30"
      statusLabel: z.string(), // "Bokat" / "Booked"
      supportingText: z.string(), // "Digitalt vårdmöte" / "Video consultation"
    })
    .nullable()
    .optional(),
});
export type HomeNowCandidate = z.infer<typeof homeNowCandidateSchema>;

// GET /patient-api/v1/home-now — the primary prioritized object, up to three
// secondary status cards, and the calm fallback. The client renders
// `candidate` when present (otherwise `fallback`); `secondary` are the
// next-ranked candidates rendered as SECONDARY status cards — exactly one
// card ever carries the primary action (QA-baseline).
// `.default([])` keeps responses from an older API parseable; older clients
// simply ignore the field.
// TES-866 — one entry in the Home question section. Every entry opens an
// article, so it carries a label and a key and nothing else: no priority, no
// CTA of its own, no task state. Keeping it deliberately thinner than
// `HomeNowCandidate` is the point — a section entry that could also carry an
// action would drift back into competing with the one prioritised object.
export const homeQuestionEntrySchema = z.object({
  articleKey: z.string().min(1),
  label: z.string().min(1),
});
export type HomeQuestionEntry = z.infer<typeof homeQuestionEntrySchema>;

// The section itself: its own heading plus the entries beneath it.
//
// It is NOT "För dig idag" and must not be rendered as that strip — CP-60/61
// canon says so explicitly, which is the whole reason this is a separate
// surface rather than an overlay on the existing one.
export const homeQuestionSectionSchema = z.object({
  title: z.string().min(1),
  entries: z.array(homeQuestionEntrySchema).min(1),
});
export type HomeQuestionSection = z.infer<typeof homeQuestionSectionSchema>;

export const homeNowResponseSchema = z.object({
  candidate: homeNowCandidateSchema.nullable(),
  secondary: z.array(homeNowCandidateSchema).default([]),
  // TES-1286 (CP-06/09C "KANONISK HEMSKÄRMSREGEL") — Hem shows the primary
  // plus up to three secondary cards; the ranked cards beyond those four, by
  // the same selection rules, so "Visa alla uppgifter (n)" can list every
  // task. Older builds ignore it and simply show four, as before.
  more: z.array(homeNowCandidateSchema).default([]),
  fallback: homeNowCandidateSchema.nullable(),
  // TES-866 — null when the patient's packages author no question section.
  // `.default(null)` so a response from an older API still parses, the same
  // reason `secondary` carries `.default([])`.
  questions: homeQuestionSectionSchema.nullable().default(null),
});
export type HomeNowResponse = z.infer<typeof homeNowResponseSchema>;
